/**
 * Intent registry (ADR-009 §3.3). Each definition turns one declarative,
 * ID-addressed intent into existing native `Command`s. Native commands stay
 * the final authority on document invariants; `validate` adds the checks the
 * agent contract needs on top (kinds, locks, limits) and reports them as
 * stable rejection codes.
 */
import type { ActorRef, FieldChange, Intent, LayerCopyReport, LayerId, RejectionCode } from '@maipo/agent-protocol';
import type { StagedBytesSource } from './assets';
import type { DecodeBudget } from './budgets';
import type { Command } from '../core/commands';
import type { EditorDocument } from '../core/model';
import type { DocumentSession } from '../core/session';

/** What a transaction reads and writes. `'root'` stands for the document's root group. */
export interface Footprint { reads: LayerId[]; writes: LayerId[]; structural: boolean; document: boolean }

/** Aliases ("$name") resolved to layer IDs created earlier in the transaction. */
export type AliasTable = Map<string, LayerId>;

/**
 * What the dispatcher itself refuses for an actor, on top of the native
 * commands' own checks (which always apply).
 *
 * The editor UI has never enforced most locks: the layer panel renames,
 * deletes, regroups and restyles locked layers, and only the pixel,
 * transform, text and vector commands refuse them natively. Moving the UI
 * onto the dispatcher must not change that, so the user (and a replay of the
 * user's recorded actions) keeps native semantics, while agents and add-ons
 * get the stricter contract of ADR-009 §5.2. This is policy, not accident:
 * change it here or nowhere.
 */
export interface ActorPolicy {
  /**
   * 'enforce': `locked` on the layer or an ancestor, `positionLocked` for
   * moves and `pixelsLocked` for pixel edits refuse with LAYER_LOCKED, and a
   * locked group refuses changes to its child list. 'native': only the native
   * commands' lock checks apply.
   */
  locks: 'enforce' | 'native';
  /**
   * 'strict': refuse operations the UI allows but the agent contract does
   * not model (ungrouping a smart object, layer styles on an adjustment
   * layer) with UNSUPPORTED_FOR_KIND. 'native': allow whatever the native
   * command allows.
   */
  kinds: 'strict' | 'native';
}

export const USER_POLICY: ActorPolicy = Object.freeze({ locks: 'native', kinds: 'native' });
export const AGENT_POLICY: ActorPolicy = Object.freeze({ locks: 'enforce', kinds: 'strict' });

/** The user and replays of the user's own recorded actions keep today's UI semantics; agents and add-ons get the strict contract. */
export function policyFor(actor: ActorRef): ActorPolicy {
  return actor.kind === 'user' || actor.kind === 'replay' ? USER_POLICY : AGENT_POLICY;
}

export interface ValidateContext {
  actor: ActorRef;
  /** Derived from the actor with policyFor(); never chosen by the caller. */
  policy: ActorPolicy;
  aliases: ReadonlyMap<string, LayerId>;
  /** Aliases declared by earlier intents whose layers do not exist yet (pre-flight validation only). */
  pending: ReadonlySet<string>;
}

export type Diagnostic =
  | { severity: 'error'; code: RejectionCode; message: string; layer?: LayerId }
  | { severity: 'warning' | 'info'; code: string; message: string; layer?: LayerId };

/** A step that can only be built after the preceding commands ran (e.g. naming a group whose ID the native command creates). */
export interface DeferredStep { deferred(document: EditorDocument, aliases: AliasTable): CompiledStep[] }
/**
 * A compiled native command must validate before mutating and must not throw
 * or reject after mutating; `TransactionCommand` can only roll back children
 * whose `execute` completed.
 */
export type CompiledStep = Command | DeferredStep;

export function isDeferredStep(step: CompiledStep): step is DeferredStep {
  return typeof (step as Partial<DeferredStep>).deferred === 'function';
}

/** Thrown by compile or deferred steps; becomes a structured rejection and rolls the transaction back. */
export class IntentRejection extends Error {
  constructor(readonly code: RejectionCode, message: string, readonly layer?: LayerId) {
    super(message);
    this.name = 'IntentRejection';
  }
}

/** A layer captured from another open document, offered to `layer.paste` for the duration of one dispatch. */
export interface ClipboardSource {
  take(handle: string): import('../public-format/layer-clipboard').LayerClipboard | undefined;
}

/**
 * Per-call inputs for `prepare` that are not part of the request JSON. The
 * dispatcher supplies `actor`; the caller of `dispatch` may supply the rest
 * (`DispatchContext`). The UI, the in-page API and replays pass none.
 */
export interface PrepareContext {
  actor: ActorRef;
  /**
   * Bytes the local bridge staged for this call (`{ staged }` AssetInputs,
   * read by `resolveAsset`). The dispatcher admits `{ staged }` only when
   * this is the call's own `StagedAssets` (`checkStagedAssets`).
   */
  assets?: StagedBytesSource;
  /** `maipo_copy_layer`'s one-call clipboard for `layer.paste`. */
  clipboards?: ClipboardSource;
  /**
   * The transaction's decoded-byte budget (agents and add-ons; the
   * dispatcher sets it, callers cannot). Every decode or layer copy an
   * intent prepares is charged before it runs.
   */
  decode?: DecodeBudget;
}

export interface CommandDefinition<I extends Intent = Intent> {
  type: I['type'];
  version: 1;
  /** Lazy editor subsystem the compiled commands need. Informational in phase 0 (imports are static). */
  runtime?: 'vector' | 'portable-text';
  footprint(document: EditorDocument, intent: I, aliases: ReadonlyMap<string, LayerId>): Footprint;
  /** Pure checks against `document`: existence, kinds, locks, limits. */
  validate(document: EditorDocument, intent: I, context: ValidateContext): Diagnostic[];
  /**
   * Asynchronous preparation outside history (decode, shaping, staging
   * resources); runs under captureRevision(). May throw IntentRejection.
   */
  prepare?(session: DocumentSession, intent: I, signal: AbortSignal, context: PrepareContext): Promise<unknown>;
  /**
   * Whether this particular intent needs `prepare` (default: whenever `prepare`
   * exists). `document` is the live document, for intents whose need depends
   * on the target (text.set prepares only for portable text).
   */
  needsPrepare?(intent: I, document: EditorDocument): boolean;
  /**
   * Releases what `prepare` returned once the dispatch is over, committed or
   * not (decoded images, paint leases, staging documents). A compiled command
   * that took ownership of part of it must leave that part alone here.
   */
  releasePrepared?(intent: I, prepared: unknown): void;
  /** Layers this intent copied (layer.duplicate, layer.paste), reported in the result's `copies` (G12). */
  copies?(intent: I, prepared: unknown): Omit<LayerCopyReport, 'intentIndex'> | undefined;
  /** Builds native commands against the current document; may register aliases. */
  compile(document: EditorDocument, intent: I, prepared: unknown, aliases: AliasTable): CompiledStep[];
  /** Predicted field changes, used by dry runs that cannot clone the document. */
  describe(document: EditorDocument, intent: I, aliases: ReadonlyMap<string, LayerId>): FieldChange[];
}

export type IntentRegistry = ReadonlyMap<string, CommandDefinition>;

export function createRegistry(definitions: readonly CommandDefinition[]): IntentRegistry {
  const registry = new Map<string, CommandDefinition>();
  for (const definition of definitions) {
    if (registry.has(definition.type)) throw new Error(`Duplicate intent definition: ${definition.type}`);
    registry.set(definition.type, definition);
  }
  return registry;
}

export function defineIntent<I extends Intent>(definition: CommandDefinition<I>): CommandDefinition {
  return definition as unknown as CommandDefinition;
}

export function emptyFootprint(): Footprint {
  return { reads: [], writes: [], structural: false, document: false };
}

export function mergeFootprints(footprints: readonly Footprint[]): Footprint {
  const reads = new Set<LayerId>();
  const writes = new Set<LayerId>();
  for (const footprint of footprints) {
    for (const id of footprint.reads) reads.add(id);
    for (const id of footprint.writes) writes.add(id);
  }
  return {
    reads: [...reads],
    writes: [...writes],
    structural: footprints.some((footprint) => footprint.structural),
    document: footprints.some((footprint) => footprint.document),
  };
}
